import {
  ImageQualityRating,
  EnumOperatorOperationalStatus,
  EnumOperatorPricing,
} from "gql/generated";
import { useState } from "react";
import { FilterScreenType } from "shared/components";
import { Days, Months } from "shared/types";

export type TagTypes = {
  businessType: string[];
  global: string[];
  organizationSpecific: string[];
  amenity: string[];
};

export type ListFiltersVisibility = "LIVE" | "UNPUBLISHED" | "";

export type DigitalHealth = "NO_WEBSITE" | "NO_IMAGES";

export type Location = {
  address: string;
  longitude: number | undefined;
  latitude: number | undefined;
  radius: number;
};

export const MINIMUM_RADIUS = 1000;

export type ListFilters = {
  filledTagGroups: TagTypes;
  tags: TagTypes;
  pricing: EnumOperatorPricing[];
  mediaQuality: ImageQualityRating[];
  operationalStatus: EnumOperatorOperationalStatus[];
  daysOfOperation: Days[];
  seasonality: Months[];
  visibility: ListFiltersVisibility;
  digitalHealth: DigitalHealth[];
  location: Location;
};

export const defaultFilters: ListFilters = {
  filledTagGroups: {
    businessType: [],
    global: [],
    organizationSpecific: [],
    amenity: [],
  },
  tags: { businessType: [], global: [], organizationSpecific: [], amenity: [] },
  pricing: [],
  mediaQuality: [],
  operationalStatus: [],
  daysOfOperation: [],
  seasonality: [],
  visibility: "",
  digitalHealth: [],
  location: {
    address: "",
    longitude: undefined,
    latitude: undefined,
    radius: MINIMUM_RADIUS,
  },
};

export type ListFilterScreenProps = {
  filters: ListFilters;
  onUpdateFilters: (filter: Partial<ListFilters>) => void;
};

export const useFilters = (
  appliedFilters: ListFilters,
  onUpdateAppliedFilters: (filter: Partial<ListFilters>) => void,
) => {
  // State that holds filter data before it's applied to the list
  const [filters, setFilters] = useState({
    ...defaultFilters,
    ...appliedFilters,
  });

  const handleUpdateFilters = (filter: Partial<ListFilters>) => {
    setFilters((prev) => ({ ...prev, ...filter }));
  };

  // Applying the filters to the list
  const handleApplyFilters = () => {
    onUpdateAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    setFilters({ ...defaultFilters, ...appliedFilters });
  };

  // Function to clear all filters for a specific screen back to its default
  // state & apply the cleared filter
  const handleClearAllPerFilterScreen = (screen: FilterScreenType) => {
    const resetFilter: Partial<ListFilters> = {};

    switch (screen) {
      case "BACK_OFFICE_TAGS":
        resetFilter["tags"] = {
          ...filters.tags,
          organizationSpecific: defaultFilters.tags.organizationSpecific,
        };
        resetFilter["filledTagGroups"] = {
          ...filters.filledTagGroups,
          organizationSpecific:
            defaultFilters.filledTagGroups.organizationSpecific,
        };
        break;
      case "KEYWORD_TAGS":
        resetFilter["tags"] = {
          ...filters.tags,
          global: defaultFilters.tags.global,
        };
        resetFilter["filledTagGroups"] = {
          ...filters.filledTagGroups,
          global: defaultFilters.filledTagGroups.global,
        };
        break;
      case "OPERATOR_TYPE":
        resetFilter["tags"] = {
          ...filters.tags,
          businessType: defaultFilters.tags.businessType,
        };
        resetFilter["filledTagGroups"] = {
          ...filters.filledTagGroups,
          businessType: defaultFilters.filledTagGroups.businessType,
        };
        break;
      case "DAYS_OF_OP":
        resetFilter["daysOfOperation"] = defaultFilters.daysOfOperation;
        break;
      case "MEDIA_QUALITY":
        resetFilter["mediaQuality"] = defaultFilters.mediaQuality;
        break;
      case "OPENING_STATUS":
        resetFilter["operationalStatus"] = defaultFilters.operationalStatus;
        break;
      case "OPERATOR_VISIBILITY":
        resetFilter["visibility"] = defaultFilters.visibility;
        break;
      case "PRICING":
        resetFilter["pricing"] = defaultFilters.pricing;
        break;
      case "SEASONALITY":
        resetFilter["seasonality"] = defaultFilters.seasonality;
        break;
      case "DIGITAL_HEALTH":
        resetFilter["digitalHealth"] = defaultFilters.digitalHealth;
        break;
      case "LOCATION":
        resetFilter["location"] = defaultFilters.location;
        break;
      default:
        break;
    }

    const newFilters = { ...filters, ...resetFilter };

    setFilters(newFilters);
    onUpdateAppliedFilters(newFilters);
  };

  return {
    filters,
    onUpdateFilters: handleUpdateFilters,
    onApplyFilters: handleApplyFilters,
    onResetFilters: handleResetFilters,
    onClearAllPerFilterScreen: handleClearAllPerFilterScreen,
  };
};
